import React from 'react';
import BaseBlock from './BaseBlock';
import { VaultRefsLine } from './VaultRefsLine';

/**
 * ExperienceBlock renders professional experiences by mapping job details.
 * Supports: classic, timeline, compact, impact, metrics, executive variants.
 */
export const ExperienceBlock = ({ items = [], variant = 'classic' }) => {
  if (!items || items.length === 0) return null;

  // Helper to bold first 3-4 words of bullet points for 'impact' styling
  const formatImpactBullet = (bulletText) => {
    if (!bulletText) return '';
    const words = bulletText.split(' ');
    if (words.length <= 3) return <strong>{bulletText}</strong>;
    const firstPart = words.slice(0, 3).join(' ');
    const restPart = words.slice(3).join(' ');
    return (
      <span>
        <strong>{firstPart}</strong> {restPart}
      </span>
    );
  };

  // 1. Compact Layout (Compressed single-line rows with details below)
  if (variant === 'compact') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-item-mb, 12px)' }}>
        {items.map((job, idx) => {
          const bullets = job.bullets || [];
          return (
            <div key={idx} style={{ width: '100%' }}>
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'baseline', 
                  fontSize: 'var(--font-size-body, 9pt)',
                  borderBottom: '1px dashed var(--color-border, #E2E8F0)',
                  paddingBottom: '4px',
                  width: '100%',
                  marginBottom: bullets.length > 0 ? '4px' : '0'
                }}
              >
                <div>
                  <strong style={{ color: 'var(--color-text-main, #2D3748)' }}>{job.company}</strong>
                  <span style={{ color: 'var(--color-text-light, #718096)', margin: '0 8px' }}>|</span>
                  <span style={{ color: 'var(--color-secondary, #4A5568)' }}>{job.role}</span>
                  {job.location && (
                    <>
                      <span style={{ color: 'var(--color-text-light, #718096)', margin: '0 8px' }}>|</span>
                      <span style={{ fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #718096)' }}>{job.location}</span>
                    </>
                  )}
                </div>
                <span style={{ fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #718096)', whiteSpace: 'nowrap' }}>
                  {job.startDate} – {job.endDate || 'Present'}
                </span>
              </div>
              {bullets.length > 0 && (
                <ul style={{ margin: '0', paddingLeft: '16px', listStyleType: 'var(--bullet-style, disc)' }}>
                  {bullets.map((bullet, bIdx) => (
                    <li 
                      key={bIdx} 
                      style={{ 
                        fontSize: 'var(--font-size-bullet, 8.8pt)', 
                        lineHeight: 'var(--line-height-bullet, 1.35)', 
                        color: 'var(--color-text-main)', 
                        marginBottom: 'var(--spacing-bullet-gap, 3px)' 
                      }}
                    >
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}
              <VaultRefsLine refs={job.vault_refs} />
            </div>
          );
        })}
      </div>
    );
  }

  // 2. Timeline Layout (Vertical left-timeline connector)
  if (variant === 'timeline') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', borderLeft: '2px solid var(--color-border, #E2E8F0)', paddingLeft: '16px', marginLeft: '6px', gap: 'var(--spacing-item-mb, 16px)' }}>
        {items.map((job, idx) => (
          <div key={idx} style={{ position: 'relative', width: '100%' }}>
            {/* Circle bullet */}
            <div 
              style={{ 
                position: 'absolute', 
                left: '-23px', 
                top: '4px', 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                backgroundColor: 'var(--color-primary, #16324F)',
                border: '2px solid var(--color-bg-page, #FFFFFF)'
              }} 
            />
            <BaseBlock
              title={job.company || ''}
              rightMeta={`${job.startDate || ''} – ${job.endDate || 'Present'}`}
              subtitle={job.role || ''}
              leftMeta={job.location ? `• ${job.location}` : ''}
              bullets={job.bullets || []}
              tags={job.technologies || job.tech || []}
            />
            <VaultRefsLine refs={job.vault_refs} />
          </div>
        ))}
      </div>
    );
  }

  // 3. Impact Layout (Bolds action words)
  if (variant === 'impact') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-item-mb, 12px)' }}>
        {items.map((job, idx) => (
          <div key={idx} className="base-block-item" data-split-child="true" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
              <strong style={{ fontSize: 'var(--font-size-subheading, 9.5pt)', color: 'var(--color-text-main, #2C3E50)', fontWeight: 'var(--weight-subheading, 700)' }}>{job.company}</strong>
              <span style={{ fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light, #7F8C8D)', fontStyle: 'italic' }}>
                {job.startDate} – {job.endDate || 'Present'}
              </span>
            </div>
            <div style={{ fontSize: 'var(--font-size-body, 9pt)', color: 'var(--color-secondary, #203C56)', fontWeight: 'var(--weight-medium, 500)', margin: '2px 0 6px 0' }}>
              <span>{job.role}</span>
              {job.location && <span style={{ fontSize: 'var(--font-size-meta, 8pt)', color: 'var(--color-text-light)', marginLeft: '6px' }}>• {job.location}</span>}
            </div>
            <ul style={{ margin: '0', paddingLeft: '20px', listStyleType: 'square' }}>
              {(job.bullets || []).map((bullet, i) => (
                <li key={i} style={{ fontSize: 'var(--font-size-bullet, 8.8pt)', lineHeight: 'var(--line-height-bullet, 1.35)', color: 'var(--color-text-main)', marginBottom: 'var(--spacing-bullet-gap, 4px)' }}>
                  {formatImpactBullet(bullet)}
                </li>
              ))}
            </ul>
            <VaultRefsLine refs={job.vault_refs} />
          </div>
        ))}
      </div>
    );
  }

  // 4. Metrics / Callout Layout (Presents numbers on the side)
  if (variant === 'metrics') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-item-mb, 16px)' }}>
        {items.map((job, idx) => (
          <div key={idx} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%' }}>
            {/* Side stats box */}
            <div style={{ width: '60px', textAlign: 'center', backgroundColor: 'var(--color-border, #F1F5F9)', borderRadius: '4px', padding: '6px 4px', fontSize: '7.5pt', color: 'var(--color-secondary, #1E293B)', fontWeight: '700', flexShrink: 0 }}>
              <span style={{ display: 'block', fontSize: '11pt', color: 'var(--color-primary)' }}>{idx + 1}</span>
              ROLE
            </div>
            <div style={{ flex: 1 }}>
              <BaseBlock
                title={job.company || ''}
                rightMeta={`${job.startDate || ''} – ${job.endDate || 'Present'}`}
                subtitle={job.role || ''}
                leftMeta={job.location ? `• ${job.location}` : ''}
                bullets={job.bullets || []}
              />
              <VaultRefsLine refs={job.vault_refs} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // 5. Classic / Executive / General Fallback
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-item-mb, 12px)' }}>
      {items.map((job, idx) => (
        <div key={idx}>
          <BaseBlock
            title={job.company || job.organization || job.employer || ''}
            rightMeta={`${job.startDate || ''}${job.startDate && job.endDate ? ' – ' : ''}${job.endDate || ''}`}
            subtitle={job.role || ''}
            leftMeta={job.location ? `• ${job.location}` : ''}
            bullets={job.bullets || []}
            tags={job.technologies || job.tech || []}
            style={variant === 'executive' ? { fontFamily: 'var(--font-family-primary, Georgia, serif)', marginBottom: '4px' } : {}}
          />
          <VaultRefsLine refs={job.vault_refs} />
        </div>
      ))}
    </div>
  );
};
export default ExperienceBlock;
